import {uniq} from 'lodash';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import React, {useEffect, useState, useMemo, useCallback} from 'react';

import {PageListProvider} from './PageListProvider';
import {RootState, useAppDispatch} from '../../../store';
import {SegmentationPrepare} from './SegmentationPrepare';
import {loadAnnotations} from '../../../store/dataPrepare';
import Waiting from '../../../base-components/StudioWaiting/Waiting';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import {SegmentationProvider, useSegmentationContext} from './model/context';
import {DataPreparationOrigin} from '../../../types/dataset/DataPreparationContainer';
import {
  CategoryType,
  AnnotationType,
  AnnotationDataType,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';

import './SegmentationView.scss';

type SegmentationViewProps = {
  projectId: string;
  inProgress: boolean;
  origin: DataPreparationOrigin;
  dataPreparationContainerId?: string;
  onBack: () => void;
  onSubmit: (annotations: AnnotationDataType) => void;
  onSaveAnnotations: (annotations: AnnotationDataType) => void;
  onUpdateAnnotations: (annotations: AnnotationType[], selectedImageId: number) => void;
};

export function SegmentationView({...props}: SegmentationViewProps) {
  return (
    <SegmentationProvider>
      <SegmentationViewOrquestor {...props} />
    </SegmentationProvider>
  );
}

export const SegmentationViewOrquestor = ({
  origin,
  projectId,
  inProgress,
  dataPreparationContainerId,
  onBack,
  onSubmit,
  onUpdateAnnotations,
}: SegmentationViewProps) => {
  const intl = useIntl();
  const [isSegmentationSubmitConfirmOpen, setIsSegmentationSubmitConfirmOpen] = useState(
    false
  );
  const [processedAnnotations, setProcessedAnnotations] = useState<{
    merged: AnnotationType[];
    discarded: AnnotationType[];
    final?: AnnotationType[];
  }>({merged: [], discarded: [], final: []});
  const [confirmationAction, setConfirmationAction] = useState<() => void>(() => {});

  const dispatch = useAppDispatch();
  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );
  const currentCategories: CategoryType[] = useMemo(
    () => annotationData?.categories || [],
    [annotationData]
  );
  const currentCategoriesMap = useMemo(
    () =>
      currentCategories.reduce<{
        [key: string]: CategoryType;
      }>((categories, category) => ({...categories, [category.name]: category}), {}),
    [currentCategories]
  );
  const currentImages = useMemo(() => annotationData?.images || [], [annotationData]);

  const {activeImageId} = useSegmentationContext();

  const validateLabelAndCategories = useCallback(
    (
      annotations: AnnotationType[],
      actionCallback: (annotations: AnnotationType[]) => void
    ) => {
      const hasOrphanAnnotations = !annotations?.every(el => !!el.category_id);
      const imagesUsedList = uniq(annotations.map(el => el.image_id));
      const hasOrphanImages = imagesUsedList.length !== currentImages.length;

      setProcessedAnnotations({...processedAnnotations, final: annotations});

      if (hasOrphanAnnotations || hasOrphanImages) {
        setIsSegmentationSubmitConfirmOpen(true);
        setConfirmationAction(() => () => actionCallback(annotations));
      } else {
        actionCallback(annotations);
      }
    },
    [currentImages, processedAnnotations]
  );

  const handleSegmentationSubmit = useCallback(
    (final: AnnotationType[]) => {
      if (
        annotationData &&
        final?.length &&
        currentCategories.length &&
        currentImages.length
      ) {
        const validAnnotations = final.filter(
          annotation => annotation?.category_id !== undefined
        );

        const validImageId = uniq(
          validAnnotations?.map(annotation => annotation.image_id)
        );
        const validCategoryId = uniq(
          validAnnotations?.map(annotation => annotation.category_id)
        );
        const validImages = currentImages.filter(image =>
          validImageId.includes(image.id)
        );
        const validCategories = currentCategories.filter(category =>
          validCategoryId.includes(category.id)
        );

        onSubmit({
          images: validImages,
          categories: validCategories,
          annotations: validAnnotations,
        });
      }
    },
    [annotationData, currentCategories, currentImages, onSubmit]
  );

  const onDone = useCallback(
    async (merged: AnnotationType[], discarded: AnnotationType[]) => {
      await new Promise<void>(resolve => {
        setProcessedAnnotations(() => {
          resolve();

          return {merged, discarded};
        });
      });

      validateLabelAndCategories(merged, handleSegmentationSubmit);
    },
    [validateLabelAndCategories, handleSegmentationSubmit]
  );

  const updateAPIAnnotations = useCallback(
    (newAnnotations: AnnotationType[]) => {
      if (newAnnotations && activeImageId) {
        const imageId = Number(activeImageId);

        onUpdateAnnotations(newAnnotations, imageId);
      }
    },
    [activeImageId, onUpdateAnnotations]
  );

  const [areAnnotationsReady, setAreAnnotationsReady] = useState(false);
  useEffect(
    function loadAnnotationsData() {
      if (dataPreparationContainerId && origin) {
        dispatch(loadAnnotations({projectId, origin})).then(() => {
          setAreAnnotationsReady(true);
        });
      }
    },
    [dataPreparationContainerId, projectId, origin, dispatch]
  );

  const isValid = useMemo(() => Boolean(annotationData?.images?.length), [
    annotationData,
  ]);
  const disableForward = useMemo(() => !isValid || inProgress, [isValid, inProgress]);

  const BackFromDetectionView = useCallback(() => {
    onBack();
  }, [onBack]);

  return !areAnnotationsReady ? (
    <Waiting
      diameter={100}
      className="data-prepare__segmentation-view__loading-annotations"
      loadingText={intl.formatMessage({
        id: 'dataPrep.segmentationView.loadingAnnotations',
      })}
    />
  ) : (
    <PageListProvider>
      <SegmentationPrepare
        origin={origin}
        projectId={projectId}
        images={currentImages}
        disableBack={inProgress}
        labels={currentCategories}
        disableForward={disableForward}
        labelsMap={currentCategoriesMap}
        dataPreparationContainerId={dataPreparationContainerId}
        onForward={onDone}
        onBack={BackFromDetectionView}
        onUpdateAnnotations={updateAPIAnnotations}
      />
      <ConfirmDialog
        type="confirm"
        open={isSegmentationSubmitConfirmOpen}
        submitLabel={intl.formatMessage({id: 'form.confirm'})}
        title={intl.formatMessage({id: 'dataPrep.segmentation.title'})}
        message={intl.formatMessage({id: 'dataPrep.finalize.warning.desc.detection'})}
        onClose={() => setIsSegmentationSubmitConfirmOpen(false)}
        onOk={() => {
          setIsSegmentationSubmitConfirmOpen(false);
          confirmationAction();
        }}
      />
    </PageListProvider>
  );
};
